// frontend/src/components/login/SignIn.jsx
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { supabase } from "../../supaBaseClient";
import { API_BASE } from "../../config";

// use centralized API_BASE from config

const SignIn = ({ setUserUID }) => {
  const navigate = useNavigate();

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Prevent duplicate welcomes/routes per page load
  const welcomedRef = useRef(false);

  // ----- Helpers -----
  const fetchUserRole = async (uid) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/role/${uid}`);
      if (!res.ok) throw new Error("Failed to fetch user role.");
      const data = await res.json();
      return data.role; // "admin" | "user"
    } catch (err) {
      console.error("Error fetching user role:", err);
      return null;
    }
  };

  const fetchUserName = async (uid) => {
    try {
      const res = await fetch(`${API_BASE}/api/users/${uid}`);
      if (!res.ok) throw new Error("Failed to fetch user name.");
      const data = await res.json();
      // Return only the first name to match Google sign-in format
      return data.first_name || "";
    } catch (err) {
      console.error("Error fetching user name:", err);
      return "";
    }
  };

  const routeByRole = async (uid, displayName) => {
    if (welcomedRef.current) return; // guard against duplicates
    const role = await fetchUserRole(uid);

    if (welcomedRef.current) return; // guard again after async
    
    // If no displayName provided (email sign-in), fetch from database
    let userName = displayName;
    if (!userName) {
      userName = await fetchUserName(uid);
    }

    if (role === "admin") {
      welcomedRef.current = true;
      setUserUID?.(uid);
      navigate("/admintopics", { replace: true });
    } else if (role === "user") {
      welcomedRef.current = true;
      setUserUID?.(uid);
      navigate("/home", { replace: true });
    } else if (role === null) {
      // error already shown
    } else {
      setError("Account not found. Please sign up.");
      // Redirect to signup page after a short delay
      setTimeout(() => {
        navigate("/signup", { replace: true });
      }, 2000);
    }
  };

  // On mount: if returning from Google OR already logged in, handle session
  useEffect(() => {
    (async () => {
      // If we came back from OAuth, Supabase appends ?code=...&state=...
      const params = new URLSearchParams(window.location.search);
      if (params.has("code")) {
        // Clean the URL so re-renders don't retrigger
        window.history.replaceState({}, "", window.location.pathname);
      }

      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("Supabase getSession error:", error);
        return;
      }
      const user = data?.session?.user;
      if (user) {
        const name = user.user_metadata?.full_name || user.user_metadata?.name || "";
        await routeByRole(user.id, name);
      }
    })();
  }, []); // same pattern as your SignUp.jsx (no exhaustive-deps)

  // ----- Email/Password sign-in -----
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError("");
    welcomedRef.current = false; // fresh attempt

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        // helpful messages for common cases
        if (error.message?.toLowerCase().includes("email not confirmed")) {
          toast.dismiss();
          toast.error("Please confirm your email before signing in.", { autoClose: 2000 });
        }
        throw error;
      }

      const user = data?.user;
      if (!user) throw new Error("No user returned from Supabase signIn.");

      // For email sign-in, don't pass name - it will be fetched from database
      await routeByRole(user.id, "");
    } catch (err) {
      console.error("Error signing in with email/password:", err);
      setError("Failed to sign in. Please check your credentials.");
    }
  };

  // ----- Password reset -----
  const handleResetPassword = async () => {
    if (!email) {
      toast.dismiss();
      toast.error("Please enter your email to reset your password.", { autoClose: 1500 });
      return;
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.dismiss();
      toast.success(`Password reset email sent to ${email}`, { autoClose: 2000 });
    } catch (err) {
      console.error("Error sending password reset email:", err);
      toast.dismiss();
      toast.error("Failed to send password reset email. Please try again.", { autoClose: 2000 });
    }
  };

  // ----- Google sign-in via redirect -----
  const handleGoogleSignInRedirect = async () => {
    try {
      welcomedRef.current = false; // fresh attempt
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/signin`,
          queryParams: { access_type: "offline", prompt: "consent" },
        },
      });
      if (error) throw error;
      // Browser will redirect away.
    } catch (err) {
      console.error("Error starting Google sign-in:", err);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <Helmet>
        <title>Sign In | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/signin" />
        <meta name="description" content="Sign in to MLMaths using Google or email to continue your learning journey and track your progress." />
      </Helmet>
      <header className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-teal-600">Welcome Back to MLMaths</h1>
        <p className="mt-2 text-base sm:text-lg text-gray-700">Sign in to continue your learning journey</p>
      </header>

      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-11/12 max-w-md">
        <form onSubmit={handleEmailSignIn}>
          <div className="flex flex-col mb-4">
            <label htmlFor="email" className="text-sm font-semibold text-gray-700">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border border-gray-300 rounded-md p-2 mt-1"
              required
            />
          </div>

          <div className="flex flex-col mb-4">
            <label htmlFor="password" className="text-sm font-semibold text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border border-gray-300 rounded-md p-2 mt-1"
              required
            />
          </div>

          {error && <p className="text-red-600 mb-4">{error}</p>}

          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors duration-500 ease-in-out mb-4"
          >
            Sign in with Email
          </button>
        </form>

        <button
          onClick={handleGoogleSignInRedirect}
          className="w-full flex items-center justify-center bg-white border border-gray-300 rounded-lg py-3 font-semibold hover:bg-gray-100 transition-colors duration-500 ease-in-out mb-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 mr-3">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Sign in with Google
        </button>

        <div className="text-center mb-4">
          <p onClick={handleResetPassword} className="text-sm text-teal-600 hover:underline cursor-pointer">
            Forget your password?
          </p>
          <p className="mt-6 text-gray-600">
            Don't have an account?{" "}
            <span onClick={() => navigate("/signup")} className="text-teal-600 hover:underline cursor-pointer">
              Sign Up
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;